import React, { useEffect, useState, useRef } from 'react';
import { useClickAway } from 'react-use';
import { useRecoilState, useRecoilValue } from 'recoil';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faTimesCircle,
  faCreditCard,
  faMapPin,
} from '@fortawesome/free-solid-svg-icons';  
import bookingApi from '../../../api/bookingApi';
import {
  selectedRentalTimeSlot,
  userPaymentsAtom,
  notificationsAtom,
  userAtom,
  userSubscriptionsAtom
} from '../../../atoms/Atoms';
import { Spinner } from '../../shared-components/Spinner';
import Button from '../../shared-components/buttons/Button';
import Input from '../../shared-components/form/Input';
import {
  backdropVariants,
  sidebarVariants,
  modalVariants
} from '../../../utils';
import envConfig from '../../../envConfig';
import './shared-components/events.css';
import Loader from '../../shared-components/loader/Loader';

function Events() {
  const { t, i18n } = useTranslation();
  const endDateOptions = [
    { title: `4 ${t('days')}`, date: dayjs().utc().add(4, 'day').format('YYYY-MM-DDTHH:00:00.00') },
    { title: t('today'), date: dayjs().utc().add(1, 'day').format('YYYY-MM-DDTHH:00:00.00') },
    { title: `3 ${t('days')}`, date: dayjs().utc().add(3, 'day').format('YYYY-MM-DDTHH:00:00.00') },
    { title: `5 ${t('days')}`, date: dayjs().utc().add(5, 'day').format('YYYY-MM-DDTHH:00:00.00') },
    { title: `14 ${t('days')}`, date: dayjs().utc().add(14, 'day').format('YYYY-MM-DDTHH:00:00.00') },
  ]
  const navigate = useNavigate();
  const showPaymentButton = envConfig.ShowPaymentButtonOnBookingReservation;
  const ref = useRef();
  const paymentsRef = useRef();
  const utc = require('dayjs/plugin/utc')
  dayjs.extend(utc)
  // const startDate = dayjs().utc().format('YYYY-MM-DDTHH:00:00.00');
  const user = useRecoilValue(userAtom);
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [payment, setPayment] = useRecoilState(userPaymentsAtom);
  const [selectedTimeSlot, setSelectedTimeSlot] = useRecoilState(selectedRentalTimeSlot);
  const [endDate, setEndDate] = useState(endDateOptions[4]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [timeSlotDays, setTimeSlotDays] = useState([]);
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [allowBooking, setAllowBooking] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const bookingPurchase = useRef(null);
  const [currentCancelToken, setCurrentCancelToken] = useState(null);
  const userSubscriptions = useRecoilValue(userSubscriptionsAtom);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [selectedBenefit, setSelectedBenefit] = useState(null);
//   const [activeItemIndex, setActiveItemIndex] = useState(0);
//   const breakpoint = useBreakpoint();
  const closePaymentsModal = () => {
    setShowPaymentsModal(false);
  };

  function handleCheckboxChange() {
    setIsChecked(!isChecked);
  }

  function handleToggle() {
    setIsExpanded(!isExpanded);
  }

  function checkSubscriptions() {
    if (userSubscriptions) {
      for (let i = 0; i < userSubscriptions?.length; i++) {
        let foundBenefit = false;
        for (let j = 0; j < userSubscriptions[i]?.remainingBenefits.length; j++) {
          if (userSubscriptions[i]?.remainingBenefits[j]?.type === 4) {
            const isBenefitMatch = (!userSubscriptions[i]?.remainingBenefits[j]?.applicableEventTypeId || userSubscriptions[i]?.remainingBenefits[j]?.applicableEventTypeId === 2);
            if (isBenefitMatch) {
              if (userSubscriptions[i]?.remainingBenefits[j]?.canBeUsed) {
                setSelectedBenefit(userSubscriptions[i]?.remainingBenefits[j]);
                foundBenefit = true;
              }
            }
          }
        }
        if (foundBenefit) {
          setSelectedSubscription(userSubscriptions[i]);
        }
      }
    } else {
      setSelectedSubscription(null);
      setSelectedBenefit(null);
    }
  }

  const agreementsList = [
    { text: t('event_agreement_option_1'), index: '1' },
    { text: t('event_agreement_option_2'), index: '2' },
    { text: t('event_agreement_option_3'), index: '3' }
  ];

//   function fetchPayments() {
//     setLoading(true);
//     api
//       .get('users/me/cards')
//       .then((response) => {
//         setLoading(false);
//         if (response.data.error) {
//           setNotifications([
//             ...notifications,
//             {
//               title: `${t('load_error')}`,
//               description: response.data.error,
//               error: true,
//             },
//           ]);
//         } else {
//           setPayment({ ...payment, cards: response.data.data.results });
//         }
//       })
//       .catch((err) => {
//         setLoading(false);
//         setNotifications([
//           ...notifications,
//           {
//             title: `${t('load_error')}`,
//             description: err.message,
//             error: true,
//           },
//         ]);
//       });
//   }

   function searchTimeSlots() {
    if (!endDate) {
      return;
    }

    if (currentCancelToken) {
      currentCancelToken.abort();
      setLoading(true);
    }

    const controller = new AbortController();
    setCurrentCancelToken(controller);

    setTimeSlotDays([]);
    setLoading(true);
    bookingApi
      .post('events/search/days/grouped', {
        searchTerm: null,
        type: 2,
        hostId: null,
        locationId: null,
        // endDate: endDate?.date
      }, {
          signal: controller.signal
        })
        .then((response) => {
          setLoading(false);
          setEvents(_.sortBy(response.data.data, 'name'));
        //   if (response.data.data.length > 0) {
        //     setSelectedEvent(_.sortBy(response.data.data, 'name')[0]);
        //   }
        })
        .catch((error) => {
          setLoading(false);
          console.error(error);
        });
  }

  const closeAll = () => {
    closePaymentsModal();
    setSelectedTimeSlot(null);
  };

  function createReservation(card, payPalInvoice, payPalReference, athMovilReference) {
    setLoading(true);
    bookingApi.post('purchases/apply/payment', {
      PurchaseId: bookingPurchase.current.purchaseId,
      UserCardId: card?.id,
      PaypalInvoiceNumber: payPalInvoice,
      PaypalReferenceNumber: payPalReference,
      AthMovilReferenceNumber: athMovilReference,
      Amount: selectedTimeSlot?.event?.price
    })
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          if (response.data.message) {
            setNotifications([
              ...notifications,
              {
                title: '',
                description: response.data.message,
                error: false,
              },
            ]);
          }
          closeAll();
          searchTimeSlots();
        } else {
          setNotifications([
            ...notifications,
            {
              title: `${t('load_error')}`,
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((err) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: `${t('load_error')}`,
            description: err.message,
            error: true,
          },
        ]);
      });
  }

  const close = () => {
    if (showPaymentsModal) {
      closePaymentsModal();
    } else {
      setSelectedTimeSlot(null);
    }
  };

  useClickAway(ref, () => {
    close()
  });

  useClickAway(paymentsRef, () => {
    closePaymentsModal()
  });

  useEffect(() => {
    if (endDate) {
      searchTimeSlots();
    }
  }, [endDate]);

  useEffect(() => {
    checkSubscriptions();
  }, [userSubscriptions]);

  // useEffect(() => {
  //   setEndDate(endDateOptions[4]);
  // }, []);

  useEffect(() => {
    if (showPaymentsModal) {
    //   fetchPayments();
    }
  }, [showPaymentsModal]);

  useEffect(() => {
    if (selectedEvent) {
      const tempDays = _.sortBy(selectedEvent?.timeslotDays, 'startDate');
      setTimeSlotDays(tempDays);
      if (selectedDate === null) {
        setSelectedDate(0); 
      }
      if (selectedTimeSlot !== null) {
        const filtered = _.filter(tempDays[selectedDate].timeSlots,
          (slot) => slot.id === selectedTimeSlot.id);
        setSelectedTimeSlot(filtered[0]);
      }
    }
  }, [selectedEvent]);

  useEffect(() => {
    if (!selectedTimeSlot || !isChecked) {
      setAllowBooking(false);
    } else {
      let hasInfo1Answer = true;
      let hasInfo2Answer = true;
      if (selectedTimeSlot?.event?.requireAdditionalInformation1 &&
        (!selectedTimeSlot.additionalInfo1 || selectedTimeSlot.additionalInfo1?.length === 0)) {
        hasInfo1Answer = false;
      }
      if (selectedTimeSlot?.event?.requireAdditionalInformation2 &&
        (!selectedTimeSlot.additionalInfo2 || selectedTimeSlot.additionalInfo2?.length === 0)) {
        hasInfo2Answer = false;
      }
      setAllowBooking(hasInfo1Answer && hasInfo2Answer);
    }
  }, [isChecked, selectedTimeSlot]);
  
  return (
    <AnimatePresence key="events">
      <div
        className="my-2 mx-4 mb-2"
      >
        <div className="flex">
          <div>
            <div className="font-semibold mt-1 ml-2 md:text-lg text-md">{t('events')}</div>
            <div className="font-light mt-1 ml-2 md:text-md text-xs">{t('events_subheader')}</div>
          </div>
          <div className="ml-auto mt-1">  
          <button
            key="reload"
            type="button"
            hidden={loading}
            onClick={() => {
            searchTimeSlots();
            }}
            className="md:text-md text-xs bg-green-500 hover:bg-green-600 text-white font-semibold py-1 px-4 border border-green-400 rounded shadow md:mx-2"
          >
            <span>{t('refresh')}{loading ? <Spinner className="ml-2 inline" /> : <></>}</span>
          </button>
          </div>
        </div>
        <div
          className="grid overflow-y-auto grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 mt-2"
          style={{ height: '70vh' }}
        >
          {events && _.map(events, (e, i) => (
            <button
              type="button"
              key={`${e.id} ${i}`}
              onClick={() => {
                navigate(`${e.id}`)
              }}
              className="events-style bg-yellow-600 rounded-lg text-center shadow"
            >
              <div className="p-4">
                <img className="mx-auto object-cover h-auto max-h-72 rounded-lg" hidden={!e.imageUrl} src={e.imageUrl} alt={e.name} />
                <h3 className="mt-2 text-xl font-bold text-white">{e.name}</h3>
                <dl className="mt-1 flex flex-grow flex-col justify-between">
                <dd className="text-sm text-white">{e.title}</dd>
                <dd className="mt-1">
                    <span className="px-2 py-0 text-md font-medium text-white tracking-tight ellipsis line-clamp-2">
                    {e.description}
                    </span>
                </dd>
                <dd className="mt-3">  
                    <div className="rounded-full bg-gray-700 px-2 py-2 text-sm font-medium text-white h-10">  
                      <FontAwesomeIcon icon={faMapPin} className="mr-2" />
                      {e.location.name}
                    </div>
                </dd>
                </dl>
              </div>
            </button>
          ))}
        </div>
      </div>
      <div className="my-2 mx-4 h-full" key="menu">
        <div className="flex space-x-2 mt-2">
          <div className="hidden">
            <div className="relative z-20 mt-2 md:block hidden">
              <Menu as="div" className="relative flex justify-end items-center">
                {({ open }) => (
                  <>
                    <Menu.Button className="w-full flex items-center justify-between rounded-md shadow-sm px-4 py-3 space-x-4 bg-white text-sm font-medium hover:bg-gray-50 outline-none focus:outline-none">
                      <span>{endDate?.title}</span>
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className={open ? 'transform rotate-180 transition duration-500 easy-in-out' : 'transition duration-500 easy-in-out'}
                      />
                    </Menu.Button>
                    <Transition
                      show={open}
                      as={React.Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items
                        static
                        className="absolute right-0 top-10 w-full mt-2 origin-bottom bg-white divide-y divide-gray-100 rounded-xl shadow outline-none max-h-60 no-scrollbar"
                      >
                        {_.map(endDateOptions, (e, i) => (
                          <Menu.Item
                            key={`${e.title} ${i}`}
                            as="button"
                            type="button"
                            className={`${e.title === endDate?.title && 'text-accent'} disabled:cursor-not-allowed text-sm font-light tracking-tight text-left flex items-center space-x-2 w-full p-4 leading-none hover:text-accent transition duration-500 easy-in-out truncate line-clamp-1`}
                            onClick={() => setEndDate(e)}
                          >
                            <span>{e.title}</span>
                          </Menu.Item>
                        ))}
                      </Menu.Items>
                    </Transition>
                  </>
                )}
              </Menu>
            </div>
          </div>
        </div>
      </div>
      {selectedTimeSlot && !showPaymentsModal && !payment.modal && (
        <motion.div
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          key="sidebar"
          exit="hidden"
          className="fixed bg-gray-800 inset-0 bg-opacity-75 z-20"
        >
          <motion.div
            ref={ref}
            variants={sidebarVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="absolute right-0 h-full flex flex-col w-full md:w-1/3 bg-white"
          >
            <div className="px-4 py-6 flex items-center justify-between bg-gray-100">
              {t('details')}
              <button
                type="button"
                onClick={() => {
                  setSelectedTimeSlot(null);
                  setIsChecked(false);
                }}
                className="hover:opacity-75 outline-none focus:outline-none"
              >
                <FontAwesomeIcon icon={faTimesCircle} className="text-xl" />
              </button>
            </div>
            <div className="flex flex-col flex-1 overflow-auto p-4">
              {selectedTimeSlot?.event?.imageUrl && (
                // Shows image if one is set to the event or location
                <img alt={selectedTimeSlot?.event?.name} src={selectedTimeSlot?.event?.imageUrl} className="h-52 w-auto object-cover rounded-lg" />
              )}
              <div className="flex">
                  <div className="my-2 text-md font-bold">{selectedTimeSlot?.event?.name}</div>
                  <div className="my-2 ml-auto text-md font-bold">${selectedTimeSlot?.event?.price.toFixed(2)}</div>
              </div>
              <div className="my-2 text-xs md:text-sm tracking-tight text-justify">{selectedTimeSlot?.event?.description}</div>   
              <div className="text-sm mt-1">{t('date')}: {`${dayjs(selectedTimeSlot?.startTime).add(-4, 'hour').format('dddd MMMM D, YYYY')}`}</div>
              <div className="text-sm mt-1">{t('slot')}: {`${dayjs(selectedTimeSlot?.startTime).add(-4, 'hour').format('h:mm a')}-${dayjs(selectedTimeSlot?.endTime).add(-4, 'hour').format('h:mm a')}`}</div>        
              <div className="text-sm mt-1">{t('location')}: {`${selectedEvent?.location.name}`}</div>
              <div className={selectedTimeSlot?.comment ? '' : 'hidden'}>{t('comments')}: {selectedTimeSlot?.comment}</div>
              {!showPaymentButton && (
                <>
                  <div className="my-2 text-md font-medium">{`${!selectedTimeSlot?.isFull ? t('contact_us_for_booking') : ''}`}</div>
                  <a href="mailto:bookings@mecacomplex.com" className={`${selectedTimeSlot?.isFull ? 'hidden' : 'font-medium text-blue-600 dark:text-blue-500 hover:underline'}`}>bookings@mecacomplex.com</a>
                </>
              )}
              {!user && (
                <Button
                  text={t('login_to_dropin')}
                  textColor="text-white"
                  onClick={() => {
                  navigate('/login', { from: 'events' });
                }}
                  loaderColor="bg-white"
                  className="bg-accent text-lg hover:bg-accent mt-4"
                />
              )}
              {!selectedTimeSlot?.isFull && !selectedTimeSlot?.reservedByUser && user && (
              <div>
                <div className="flex flex-col items-start mt-8 bg-red-100 rounded-xl p-4 border-t border-gray-100">
                  <label className="text-sm font-semibold">{t('event_terms')}</label>
                  <button 
                    hidden
                    type="button"
                    onClick={handleToggle}
                  >
                    {isExpanded ? 'show less' : 'show less'} List
                  </button>
                {isExpanded && (
                  <ul className="text-sm">
                    {_.map(agreementsList, (item, i) => (
                      <li key={`${item.index} ${i}`}>-{item.text}</li>
                    ))}
                  </ul>
                )}
                <div className="text-sm mt-4">
                  <span>{t('if_cancel_booking_contact')}
                  </span>
                  <a href="mailto:bookings@mecacomplex.com" className=" ml-1 font-medium text-blue-600 dark:text-blue-500 hover:underline">bookings@mecacomplex.com</a>
                </div>
                </div>          
              <div className="flex items-center mx-1 mt-4 mb-4">
                <input
                  type="checkbox"
                  className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="agreement" className="ml-2 text-sm">
                  {t('booking_i_agree_erms')}
                </label>
              </div>
                            {selectedTimeSlot?.event?.requireAdditionalInformation1 && (
                <div className="flex items-center mx-1 mb-4">
                  <Input
                    label={i18n.language === 'en' ? selectedTimeSlot?.event?.additionalInformation1EngPrompt : selectedTimeSlot?.event?.additionalInformation1SpaPrompt}
                    required
                    type="text"
                    name="additionalInfo1"
                    id="additionalInfo1"
                    value={selectedTimeSlot?.additionalInfo1}
                    onChange={(obj) => setSelectedTimeSlot({
                      ...selectedTimeSlot,
                      additionalInfo1: obj.target.value
                    })}
                  />
                </div>
              )}
              {selectedTimeSlot?.event?.requireAdditionalInformation2 && (
                <div className="flex items-center mx-1 mb-4">
                  <Input
                    label={i18n.language === 'en' ? selectedTimeSlot?.event?.additionalInformation2EngPrompt : selectedTimeSlot?.event?.additionalInformation2SpaPrompt}
                    required
                    type="text"
                    name="additionalInfo2"
                    id="additionalInfo2"
                    value={selectedTimeSlot?.additionalInfo2}
                    onChange={(obj) => setSelectedTimeSlot({
                      ...selectedTimeSlot,
                      additionalInfo2: obj.target.value
                    })}
                  />
                </div>
              )}
              </div>)}
              {selectedTimeSlot?.reservedByUser && user && (
                <div className="my-4 text-center text-white bg-purple-600 rounded-xl p-2 text-lg">
                {t('already_reserved')}
                </div>
              )}
              {showPaymentButton && !selectedTimeSlot?.isFull 
              && !selectedTimeSlot?.reservedByUser && user && (
                <Button
                  text={t('complete_payment')}
                  textColor="text-white"
                  disabled={loading || !allowBooking}
                  loading={loading}
                  onClick={() => {
                    setShowPaymentsModal(true);
                    setIsChecked(false);
                  }}
                  loaderColor="bg-white"
                  className="bg-customgreen hover:ring-2 ring-offset-2 ring-green-500 text-lg"
                />
              )}
              {showPaymentButton && (selectedTimeSlot?.isFull ||
               (selectedTimeSlot.maxCapacity === selectedTimeSlot.currentCapacity)) &&
                !selectedTimeSlot?.reservedByUser && user && (
                <Button
                  text={t('class_is_full')}
                  textColor="text-white"
                  disabled
                  loaderColor="bg-white"
                  className="bg-red-400 hover:ring-2 ring-offset-2 ring-red-500 text-lg mt-4"
                />
              )}  
              {selectedTimeSlot?.reservedByUser && user && (
                <div className="my-4 text-center text-purple-600 p-2 text-lg">
                {t('already_have_reservation')}
                </div>
              )}            
            </div>
          </motion.div>
        </motion.div>
      )}
      {selectedTimeSlot && showPaymentsModal && !payment.modal && (
        <motion.div
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          key="payments"
          exit="hidden"
          className="fixed bg-gray-800 inset-0 bg-opacity-75 z-10 flex items-end md:items-center justify-center px-0 md:px-4"
        >
          <motion.div
            variants={modalVariants}
            className="text-gray-800 antialiased justify-center items-center flex overflow-x-hidden fixed inset-0 z-50 outline-none focus:outline-none"
          >
            <div
              ref={paymentsRef}
              className="flex flex-col bg-white rounded-xl mx-auto"
            >
              <header className="flex items-center justify-between bg-gray-800 text-white rounded-t-xl px-4 py-6">
                <div className="flex items-center space-x-1 font-bold tracking-tight text-xl leading-tight">
                  {t('amount_to_pay')}: ${(selectedTimeSlot?.event?.price).toFixed(2)}
                </div>
                <button
                  type="button"
                  onClick={() => closePaymentsModal()}
                  className="text-xl leading-none ring-0 outline-none focus:outline-none"
                >
                  <FontAwesomeIcon icon={faTimesCircle} />
                </button>
              </header>
              {loading && <Spinner className="mx-auto mt-2" />}
              <main className="group relative flex flex-1 bg-gray-50 rounded-b-xl p-4">
                <div className="flow">
                  <div>{t('payment_method_description')}</div>
                  <Spinner
                    spinning={loading}
                    className="mx-auto my-4"
                  />
                  {envConfig.AllowCreditCardCreate === 'true' && envConfig.AllowCreditCardsInBooking === 'true' && (
                    <button
                      type="button"
                      onClick={() => {
                        setPayment({ ...payment, modal: true });
                      }}
                      className="text-left w-full mt-4 transform hover:scale-90 shadow rounded-xl flex h-20 transition duration-500 ease-linear ring-2 ring-offset-2 ring-amber-400 border border-amber-400 outline-none focus:outline-none"
                    >
                      <div className="w-16 h-full flex flex-shrink-0 items-center justify-center text-white text-3xl bg-amber-400 rounded-l-xl">
                        <FontAwesomeIcon icon={faCreditCard} />
                      </div>
                      <div className="flex flex-col justify-center p-4 text-xs text-gray-800">
                        <div className="font-semibold">{t('add_new_payment')}</div>
                        <div>{t('new_card')}</div>
                      </div>
                    </button>
                  )}
                  {loading && (
                    <div className="flex mb-4">
                      <div className="mx-auto"><Loader /></div>
                    </div>
                  )}
                  {envConfig.PayPalForBookings === 'true' && (
                    <div className="mt-6">
                      <PayPalScriptProvider options={{ 'client-id': envConfig.PayPalKey }}>
                        <PayPalButtons
                          onClick={(data, actions) => {
                            setLoading(true);
                            return bookingApi.post('events/booking', {
                              EventTimeSlotId: selectedTimeSlot?.id,
                              UserCardId: null,
                              PaypalInvoiceNumber: null,
                              PaypalReferenceNumber: null,
                              AthMovilReferenceNumber: null,
                              Amount: selectedTimeSlot?.event?.price,
                              AdditionalInformation1Response: selectedTimeSlot?.additionalInfo1,
                              AdditionalInformation2Response: selectedTimeSlot?.additionalInfo2,
                              BypassPayment: false,
                              BenefitId: selectedBenefit?.id
                            })
                              .then((response) => {
                                setLoading(false);
                                if (response.data.success) {
                                  bookingPurchase.current = response.data.data;
                                  return actions.resolve();
                                }
                                  setNotifications([
                                    ...notifications,
                                    {
                                      title: `${t('load_error')}`,
                                      description: response.data.error,
                                      error: true,
                                    },
                                  ]);
                                  return actions.reject();
                              })
                              .catch((err) => {
                                setLoading(false);
                                setNotifications([
                                  ...notifications,
                                  {
                                    title: `${t('load_error')}`,
                                    description: err.message,
                                    error: true,
                                  },
                                ]);
                                return actions.reject();
                              });
                          }}
                          createOrder={(data, actions) => actions.order.create({
                            purchase_units: [
                                {
                                    description: `${selectedTimeSlot?.event?.name} ${dayjs(selectedTimeSlot?.startTime).add(-4, 'hour').format('MMM D YYYY')} ${dayjs(selectedTimeSlot?.startTime).add(-4, 'hour').format('h:mm a')}-${dayjs(selectedTimeSlot?.endTime).add(-4, 'hour').format('h:mm a')}`,
                                    amount: {
                                        value: `${(selectedTimeSlot?.event?.price).toFixed(2)}`,
                                    },
                                    custom_id: `purchaseId=${bookingPurchase.current.purchaseId}`
                                },
                            ],
                            application_context: { brand_name: 'MECA Complex', locale: `${i18n.language}-US`, shipping_preference: 'NO_SHIPPING' }
                          })}
                          onCancel={(data, actions) => {
                            if (bookingPurchase?.current?.purchaseId) {
                              // TODO Cancel booking
                            }
                          }}
                          onApprove={(data, actions) => actions.order.capture().then((details) => {
                            const refNumber = details.id;
                            const invoiceNumber =
                              details.purchase_units[0]?.payments?.captures[0]?.id;
                            createReservation(null, invoiceNumber, refNumber, null);
                          })}
                          onCancel={(data) => {
                            setLoading(false);
                            if (bookingPurchase?.current?.purchaseId) {
                              bookingApi.delete(`events/booking/purchase/${bookingPurchase.current.purchaseId}`);
                            }
                          }}
                          onError={(err) => {
                            setLoading(false);
                            setNotifications([
                              ...notifications,
                              {
                                title: `${t('load_error')}`,
                                description: err?.message || 'PayPal payment failed.',
                                error: true,
                              },
                            ]);
                          }}
                        />
                      </PayPalScriptProvider>
                    </div>
                  )}
                  {envConfig.AthMovilForBookings === 'true' && !loading && (
                    <></>
                  )}
                  {envConfig.AllowCreditCardsInBooking === 'true' && _.map(payment.cards, (card) => (
                    <button
                      type="button"
                      onClick={() => createReservation(card, null, null, null)}
                      key={card.id}
                      className="w-full"
                    >
                      <div
                        className="divide-x mt-4 divide-purple-200 flex shadow rounded-xl h-20 transition duration-500 ease-linear ring-2 ring-offset-2 ring-purple-400 border border-purple-400 outline-none focus:outline-none"
                      >
                        <div className="w-16 flex flex-col items-center justify-center text-white text-3xl bg-purple-600 rounded-l-xl">
                          <FontAwesomeIcon icon={faCreditCard} />
                        </div>
                        <div className="flex flex-col flex-1 items-start justify-start text-xs p-2">
                          <div className="font-semibold">{card.brand}</div>
                          <div>**** {card.last4}</div>
                          <div>{card.expirationDate}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </main>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default Events;
